import styles from './OnloadPopUps.module.scss';

import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMerchant } from 'hooks/context/merchant';
import { useMerchantState } from 'hooks/context/MerchantState';
import { useMenu } from './MenuContext/MenuContext';
import { ServiceTypeAndTimeModal } from 'routes/ServiceTypeAndTimeModal/ServiceTypeAndTimeModal';
import { ImageModal } from './ImageModal';
import { useSmartNavigate } from 'hooks/useSmartNavigate';
import { DELIVERY_MODAL_TEST_ID, S2O_MODAL_TEST_ID } from './OnloadPopUps.const';
import { MerchantLogo } from './MerchantLogo';
import { Scan2OrderPopUp } from './Scan2OrderPopUp';
import { getHeapUserId } from 'util/Analytics';
// import { Helmet } from 'react-helmet';
// import { stringifyUrl } from 'query-string';
import { AppPromotionPopup } from './AppPromotionPopup';
import { useRunningOrder } from 'hooks/context/RunningOrder';
import { useSessionStorageState } from '@clover-util/use-storage-state';
import { useCustomer } from 'hooks/context/customer/customer';

type OnloadPopUpsProps = React.HTMLAttributes<HTMLElement> & {
  visible: boolean;
  onClose: () => void;
};

/**
 * This component contains all the components that pop-up for a customer depending on state
 * - scheduledOrderModal when merchant closed
 * - delivery home screen
 * - scan 2 order
 */
export const OnloadPopUps: React.FC<OnloadPopUpsProps & {children?: never}> = ({ visible, onClose }) => {
  const { t } = useTranslation();
  const navigate = useSmartNavigate();
  const { error: menuError } = useMenu();
  const { merchant, country } = useMerchant();
  const runningOrder = useRunningOrder();
  const { token } = useCustomer();
  const [isTouched, setIsTouched] = useSessionStorageState('colo_onloadpop_istouched', !!token);
  const { showScheduledOrderClosedState, showDeliveryScreen, showRunningOrderState, isAcceptingOrders } = useMerchantState();

  const handleOnClose = useCallback(() => {
    setIsTouched(true);
    onClose();
  }, [onClose, setIsTouched]);

  const showScan2OrderPopUp = useMemo(() => showRunningOrderState && (!runningOrder.hasItems && !runningOrder.isLoading), [showRunningOrderState, runningOrder.hasItems, runningOrder.isLoading]);

  const appMenuQueryParams = useMemo(() => ({
    merchantId: merchant?.merchantUuid,
    heapId: getHeapUserId(),
    name: merchant?.name,
  }), [merchant?.merchantUuid, merchant?.name]);

  if (!visible || menuError || !merchant || isTouched) return null;

  return (
    <>
      {/* <Helmet>
        {merchant && country === 'US' && !showRunningOrderState && <meta
          name="apple-itunes-app"
          content={`app-id=428620381, app-argument=${stringifyUrl({
            url: `${window.location.protocol}//${window.location.hostname}/online-ordering/app/menu`,
            query: appMenuQueryParams,
          })}`}
        />}
      </Helmet> */}
      {showScheduledOrderClosedState && <ServiceTypeAndTimeModal
        onClose={handleOnClose}
        headerLabel={t('bannerMessage.paused', { merchantName: merchant.name })}
        subheaderLabel={t('scheduledOrder.subheader')}
        headerClass={styles.scheduledOrderHeader}
        isScheduledOrderModal />}
      {showDeliveryScreen && isAcceptingOrders && <ImageModal
        isOpen={visible}
        title={t('delivery.title')}
        iconName={'delivery'}
        subtitle={t('delivery.subtitle')}
        primaryButtonContent={t('delivery.orderDelivery')}
        secondaryButtonContent={t('delivery.notToday')}
        onClick={() => {
          navigate('~address');
          handleOnClose();
        }}
        onClose={handleOnClose}
        testId={DELIVERY_MODAL_TEST_ID}
        />
      }
      {showScan2OrderPopUp && <Scan2OrderPopUp
        isOpen={visible}
        title={t('scan2order.title', { merchantName: merchant.name })}
        onClose={handleOnClose}
        testId={S2O_MODAL_TEST_ID}
        logo={<MerchantLogo size={'medium'} />}
        >
        </Scan2OrderPopUp>}
      {merchant && country === 'US' && !showRunningOrderState && <AppPromotionPopup to="menu" query={appMenuQueryParams} onClose={handleOnClose} />}
    </>
  );
};
